/**
 * Error Boundary Component
 * 
 * Catches React component errors and automatically reports them to CloudWatch monitoring.
 * Wrap your app or key components with this to ensure all React errors are tracked.
 * 
 * Usage:
 * ```tsx
 * import { ErrorBoundary } from '@mental-mint/shared-ui';
 * 
 * function App() {
 *   return (
 *     <ErrorBoundary>
 *       <YourApp />
 *     </ErrorBoundary>
 *   );
 * }
 * ```
 * 
 * With custom fallback:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <YourApp />
 * </ErrorBoundary>
 * ```
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger, getEnvironment, ENVIRONMENTS } from '@mental-mint/shared-utils';
import { Copy, Check } from 'lucide-react';

interface ErrorBoundaryProps {
  /** Child components to protect */
  children: ReactNode;
  /** Custom fallback UI to show when error occurs */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  copied: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render shows the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // logger.error() automatically generates errorId and sends to CloudWatch
    const errorId = logger.error('React Error Boundary:', error);
    
    // Store errorId in state to display to user
    this.setState({ errorId });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback or default error UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI with modal appearance (dark mode)
      // Check if we're in development mode using environment utils
      const isDevelopment = getEnvironment() === ENVIRONMENTS.DEVELOPMENT;
      
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          fontFamily: 'Open Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            border: '1px solid #333',
          }}>
            <h2 style={{ 
              color: '#dc3545',
              margin: '0 0 16px 0',
              fontSize: '24px',
              fontWeight: 600,
            }}>
              Something went wrong
            </h2>
            
            <p style={{ 
              color: '#e0e0e0',
              margin: '0 0 16px 0',
              fontSize: '16px',
              lineHeight: '1.5',
            }}>
              We've been notified of this error and will fix it soon.
            </p>

            {this.state.errorId && (
              <div 
                onClick={() => {
                  navigator.clipboard.writeText(this.state.errorId || '');
                  this.setState({ copied: true });
                  setTimeout(() => this.setState({ copied: false }), 2000);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
                onTouchStart={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
                title="Click to copy error ID"
              >
                <div style={{ 
                  fontSize: '12px',
                  color: '#999',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Error ID
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}>
                  <code style={{ 
                    fontSize: '14px',
                    color: '#dc3545',
                    fontWeight: 600,
                    fontFamily: 'monospace',
                    flex: 1,
                  }}>
                    {this.state.errorId}
                  </code>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    color: this.state.copied ? '#28a745' : '#999',
                    flexShrink: 0,
                  }}>
                    {this.state.copied ? (
                      <>
                        {React.createElement(Check as any, { size: 14 })}
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        {React.createElement(Copy as any, { size: 14 })}
                        <span>Click to copy</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {isDevelopment && (
              <details style={{ marginBottom: '20px' }}>
                <summary style={{ 
                  cursor: 'pointer',
                  color: '#999',
                  fontSize: '14px',
                  marginBottom: '8px',
                  userSelect: 'none',
                }}>
                  Error details (dev only)
                </summary>
                <pre style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '6px',
                  overflow: 'auto',
                  fontSize: '12px',
                  color: '#e0e0e0',
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid #333',
                }}>
                  {this.state.error?.toString()}
                </pre>
              </details>
            )}

            <button
              onClick={() => window.location.reload()}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#dc3545',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#c82333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
